import { useCallback, useEffect, useMemo, useRef, useState, SyntheticEvent, KeyboardEvent } from 'react'

import { useHistory } from 'react-router-dom'

import { Organization, useChangeAuctionMutation, useUpdateBiRolesMutation } from '../../../../generated/graphql'

import useGlobalAuctionStore from '../../../../modules/auction/store/globalAuctionStore'
import { createDialog } from '../../../../spark_libs_deprecated/feature-dialog/dialog.store'

import { AuctionOrganization } from './UserAccountSelect.types'
import { filterAuctions, getOptionLabel } from './UserAccountSelect.utils'

interface ControllerArgs {
    activeOrganization: Organization
    organizations: Organization[]
    openRequest?: number
    handleClose?: () => void
}

export const useUserAccountSelectController = ({
    activeOrganization,
    organizations,
    openRequest,
    handleClose,
}: ControllerArgs) => {
    const searchInputRef = useRef<HTMLInputElement | null>(null)
    const triggerButtonRef = useRef<HTMLButtonElement | null>(null)
    const focusFrameRef = useRef<number | null>(null)
    const focusTimeoutRef = useRef<number | null>(null)
    const lastHandledOpenRequestRef = useRef(0)

    const [isOpen, setIsOpen] = useState(false)
    const [isSwitching, setIsSwitching] = useState(false)
    const [searchValue, setSearchValue] = useState('')

    const history = useHistory()
    const { auctionId, setAuctionSites, setCurrentAuctionId } = useGlobalAuctionStore()
    const [changeOrg] = useChangeAuctionMutation()
    const [updateBIRoles] = useUpdateBiRolesMutation()

    const auctionList = useMemo(
        () =>
            organizations.filter(
                (auction): auction is AuctionOrganization => auction.orgTypeCode === 'auction' && !!auction.orgId,
            ),
        [organizations],
    )

    const selectedAuctionId = auctionId || activeOrganization.orgId || ''
    const selectedAuction = auctionList.find(({ orgId }) => orgId === selectedAuctionId)

    const filteredAuctionList = useMemo(
        () =>
            filterAuctions(auctionList, {
                inputValue: searchValue,
                getOptionLabel,
            }),
        [auctionList, searchValue],
    )

    const closeDropdown = useCallback(() => {
        setIsOpen(false)
        setSearchValue('')
    }, [])

    const focusSearchInput = useCallback(() => {
        if (focusFrameRef.current !== null) {
            cancelAnimationFrame(focusFrameRef.current)
        }

        if (focusTimeoutRef.current !== null) {
            window.clearTimeout(focusTimeoutRef.current)
        }

        focusFrameRef.current = requestAnimationFrame(() => {
            searchInputRef.current?.focus()
            searchInputRef.current?.select()
        })

        focusTimeoutRef.current = window.setTimeout(() => {
            searchInputRef.current?.focus()
            searchInputRef.current?.select()
        }, 50)
    }, [])

    const openDropdown = useCallback(() => {
        if (auctionList.length < 2 || isSwitching) {
            return
        }

        setIsOpen(true)
    }, [auctionList.length, isSwitching])

    const handleOrgChange = useCallback(
        async (_evt: SyntheticEvent, organization: Organization | null) => {
            if (!organization?.orgId || organization.orgId === selectedAuctionId || isSwitching) {
                return
            }

            const newAuction = organization.orgId
            setIsSwitching(true)

            try {
                await Promise.all([
                    changeOrg({ variables: { newAuction } }),
                    updateBIRoles({ variables: { auctionId: newAuction } }),
                ])

                setCurrentAuctionId(newAuction)
                setAuctionSites(organization)
                closeDropdown()
                handleClose?.()
                history.push('/')
            } catch (error) {
                createDialog({
                    action: 'CLOSE',
                    color: 'error',
                    message: error instanceof Error ? error.message : 'Unable to switch auctions right now.',
                    timeout: 5000,
                    type: 'SNACKBAR',
                })
            } finally {
                setIsSwitching(false)
            }
        },
        [
            changeOrg,
            closeDropdown,
            handleClose,
            history,
            isSwitching,
            selectedAuctionId,
            setAuctionSites,
            setCurrentAuctionId,
            updateBIRoles,
        ],
    )

    const handleSearchEnter = useCallback(
        (evt: KeyboardEvent<HTMLElement>) => {
            evt.preventDefault()

            if (filteredAuctionList.length !== 1) {
                return
            }

            void handleOrgChange(evt, filteredAuctionList[0])
        },
        [filteredAuctionList, handleOrgChange],
    )

    const handleSearchTab = useCallback(
        (evt: React.KeyboardEvent<HTMLElement>) => {
            if (evt.shiftKey) {
                evt.preventDefault()
                closeDropdown()
                triggerButtonRef.current?.focus()
                return
            }

            const nextAuction = filteredAuctionList[0]

            if (!nextAuction) {
                evt.preventDefault()
                closeDropdown()
                triggerButtonRef.current?.focus()
                return
            }

            evt.preventDefault()
            void handleOrgChange(evt, nextAuction)
        },
        [closeDropdown, filteredAuctionList, handleOrgChange],
    )

    const handleTriggerKeyDown = useCallback(
        (evt: React.KeyboardEvent<HTMLButtonElement>) => {
            if (evt.key === 'ArrowDown' || evt.key === 'Enter' || evt.key === ' ') {
                evt.preventDefault()
                openDropdown()
            }
        },
        [openDropdown],
    )

    useEffect(() => {
        if (activeOrganization.orgId && activeOrganization.orgId.length > 0) {
            setCurrentAuctionId(activeOrganization.orgId)
            setAuctionSites(activeOrganization)
        }
    }, [activeOrganization, setAuctionSites, setCurrentAuctionId])

    useEffect(() => {
        if (!isOpen) {
            return
        }

        focusSearchInput()
    }, [focusSearchInput, isOpen])

    useEffect(() => {
        return () => {
            if (focusFrameRef.current !== null) {
                cancelAnimationFrame(focusFrameRef.current)
            }

            if (focusTimeoutRef.current !== null) {
                window.clearTimeout(focusTimeoutRef.current)
            }
        }
    }, [])

    useEffect(() => {
        if (!openRequest || openRequest === lastHandledOpenRequestRef.current || auctionList.length < 2) {
            return
        }

        lastHandledOpenRequestRef.current = openRequest

        if (isOpen) {
            focusSearchInput()
            return
        }

        openDropdown()
    }, [auctionList.length, focusSearchInput, isOpen, openDropdown, openRequest])

    return {
        auctionList,
        closeDropdown,
        filteredAuctionList,
        handleOrgChange,
        handleSearchEnter,
        handleSearchTab,
        handleTriggerKeyDown,
        isOpen,
        isSwitching,
        openDropdown,
        searchInputRef,
        searchValue,
        selectedAuction,
        selectedAuctionId,
        setSearchValue,
        triggerButtonRef,
    }
}
